import { SchemaOf } from 'yup';
import * as Yup from 'yup';
import { Formik, FormikProps } from 'formik';
import {
  FormFieldsSection,
  FormLeftSection,
  FormRow,
  StyledForm,
} from 'app/components/Forms/FormsLayout';
import { FormTextField } from 'app/components/Forms/FormTextField';
import { useTranslation } from 'react-i18next';
import { translations } from 'locales/translations';
import { Button } from 'app/components/BasicButtons/Button';
import { TwoFactorAuthenticationParams } from './type';
import React, { useRef } from 'react';
import { MyTimer } from './Timer';

export interface TwoFactorAuthenticationProps {
  initial: TwoFactorAuthenticationParams;
  onSubmit: (item: TwoFactorAuthenticationParams | null) => void;
  formRef: React.MutableRefObject<FormikProps<
    TwoFactorAuthenticationParams
  > | null>;
}

export function TwoFactorAuthenticationForm(
  props: TwoFactorAuthenticationProps,
) {
  const { initial, formRef, onSubmit } = props;
  const [canceled, setCanceled] = React.useState<boolean>(false);
  const oneRef = useRef();
  const twoRef = useRef();
  const threeRef = useRef();
  const fourRef = useRef();
  const fiveRef = useRef();
  const sixRef = useRef();
  const schema: SchemaOf<TwoFactorAuthenticationParams> = Yup.object({
    one: Yup.string().notRequired().nullable(),
    two: Yup.string().notRequired().nullable(),
    three: Yup.string().notRequired().nullable(),
    four: Yup.string().notRequired().nullable(),
    five: Yup.string().notRequired().nullable(),
    six: Yup.string().notRequired().nullable(),
  });
  const { t } = useTranslation();
  const handleSubmit = value => {
    if (onSubmit !== undefined && onSubmit !== null) {
      onSubmit(canceled ? null : value);
    }
  };
  const handleKeyUp = React.useCallback((curr, next) => {
    if (
      curr.current?.value !== undefined &&
      curr.current?.value.trim() !== ''
    ) {
      next.current?.focus();
    } else {
      if (
        curr !== undefined &&
        curr !== null &&
        curr.current !== undefined &&
        curr.current !== null &&
        curr.current?.value === ' '
      ) {
        curr.current.value = '';
      }
    }
  }, []);
  const handleLastKeyUp = React.useCallback((curr): boolean => {
    if (
      curr.current?.value !== undefined &&
      curr.current?.value.trim() !== ''
    ) {
      return true;
    } else {
      if (
        curr !== undefined &&
        curr !== null &&
        curr.current !== undefined &&
        curr.current !== null &&
        curr.current?.value === ' '
      ) {
        curr.current.value = '';
      }
      return false;
    }
  }, []);
  const handleExpire = React.useCallback(() => {
    if (onSubmit !== undefined && onSubmit !== null) {
      onSubmit(null);
    }
  }, [onSubmit]);
  const time = new Date();
  time.setSeconds(time.getSeconds() + 120);
  return (
    <>
      <Formik
        innerRef={formRef}
        validationSchema={schema}
        initialValues={initial}
        onSubmit={async (values, formikHelpers) => {
          formikHelpers.setSubmitting(true);
          handleSubmit(values);
        }}
      >
        {formik => {
          return (
            <>
              <StyledForm onSubmit={formik.handleSubmit} avoidStyles={true}>
                <FormLeftSection>
                  <FormFieldsSection>
                    <FormRow>
                      <FormTextField
                        name="one"
                        disabled={formik.isSubmitting}
                        autoComplete="off"
                        id="oneId"
                        inputProps={{
                          maxLength: 1,
                        }}
                        style={{ width: '48px' }}
                        inputRef={oneRef}
                        onKeyUp={event => {
                          handleKeyUp(oneRef, twoRef);
                        }}
                      />
                      <FormTextField
                        name="two"
                        disabled={formik.isSubmitting}
                        autoComplete="off"
                        id="twoId"
                        inputProps={{
                          maxLength: 1,
                        }}
                        style={{ width: '48px' }}
                        inputRef={twoRef}
                        onKeyUp={event => {
                          handleKeyUp(twoRef, threeRef);
                        }}
                      />
                      <FormTextField
                        name="three"
                        disabled={formik.isSubmitting}
                        autoComplete="off"
                        id="threeId"
                        inputProps={{
                          maxLength: 1,
                        }}
                        style={{ width: '48px' }}
                        inputRef={threeRef}
                        onKeyUp={event => {
                          handleKeyUp(threeRef, fourRef);
                        }}
                      />
                      <FormTextField
                        name="four"
                        disabled={formik.isSubmitting}
                        autoComplete="off"
                        id="fourId"
                        inputProps={{
                          maxLength: 1,
                        }}
                        style={{ width: '48px' }}
                        inputRef={fourRef}
                        onKeyUp={event => {
                          handleKeyUp(fourRef, fiveRef);
                        }}
                      />
                      <FormTextField
                        name="five"
                        disabled={formik.isSubmitting}
                        autoComplete="off"
                        id="fiveId"
                        inputProps={{
                          maxLength: 1,
                        }}
                        style={{ width: '48px' }}
                        inputRef={fiveRef}
                        onKeyUp={event => {
                          handleKeyUp(fiveRef, sixRef);
                        }}
                      />
                      <FormTextField
                        name="six"
                        disabled={formik.isSubmitting}
                        autoComplete="off"
                        id="sixId"
                        style={{ width: '48px' }}
                        inputProps={{ maxLength: 1 }}
                        inputRef={sixRef}
                        onKeyUp={event => {
                          if (handleLastKeyUp(sixRef)) {
                            setCanceled(false);
                            formik.submitForm();
                          }
                        }}
                      />
                    </FormRow>
                    <FormRow fullRow={true}>
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          alignItems: 'center',
                          paddingTop: 24,
                          gap: 36,
                        }}
                      >
                        <MyTimer
                          title={'Expires in '}
                          expiryTimestamp={time}
                          onExpire={handleExpire}
                        />
                        <Button
                          variant="white"
                          size="large"
                          fullWidth
                          id={'factor_cancelId'}
                          onClick={() => {
                            setCanceled(true);
                            formik.submitForm();
                          }}
                        >
                          {t(translations.Cancel) as string}
                        </Button>
                      </div>
                    </FormRow>
                  </FormFieldsSection>
                </FormLeftSection>
              </StyledForm>
            </>
          );
        }}
      </Formik>
    </>
  );
}
